import React, { useState, useEffect, useMemo } from "react";
import server from "../../utils/serverConfig";
import apiClient from "../../utils/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Loader2, Search, Filter, TrendingUp, BarChart2 } from "lucide-react";
import ETFHeatmap from "./ETFHeatmap";
import ETFTable from "./ETFTable";
import ETFComparisonChart from "./ETFComparisonChart";

const ETF_SETS = {
  intl: {
    label: 'International',
    regionLabel: 'Region',
    colHeader: 'Country',
    groups: ['Asia', 'Europe', 'Americas', 'ME/Africa', 'Regional'],
  },
  us: {
    label: 'US Sectors',
    regionLabel: 'Category',
    colHeader: 'Section',
    groups: [
      'Broad Market Benchmarks', 
      'Technology & Innovation', 
      'Healthcare & Biotech', 
      'Energy & Resources', 
      'Sector Rotation', 
      'Defense & Infrastructure', 
      'Crypto & Digital Assets'
    ],
  }
};

const ETFIntelligence = () => {
  const API = server.godseye.baseUrl.replace(/\/$/, "");
  const [currentSet, setCurrentSet] = useState("intl");
  const [data, setData] = useState(null);
  const [spyYearly, setSpyYearly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("All");
  const [sizeBy, setSizeBy] = useState("day"); // day, week, month
  const [viewMode, setViewMode] = useState("both"); // heatmap, table, both
  const [selectedTicker, setSelectedTicker] = useState(null);

  const handleTileClick = (ticker) => {
    setSelectedTicker(ticker);
    // If only heatmap is visible, switch to Both or Table to see the result
    if (viewMode === "heatmap") setViewMode("both");
    
    // Clear selection after a delay to allow re-clicking the same tile
    setTimeout(() => setSelectedTicker(null), 1000);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const endpoint = currentSet === "intl" ? "/rsc/etf/intl" : "/rsc/etf/us";
        const res = await apiClient.get(`${API}${endpoint}`);
        if (res.data && res.data.etfs) {
          setData(res.data.etfs);
          setSpyYearly(res.data.spy_yearly || []);
        } else {
          setData([]);
        }
      } catch (err) {
        console.error("ETF fetch failed", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentSet]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter(etf => {
      const matchesSearch = etf.ticker.toLowerCase().includes(search.toLowerCase()) || 
                           etf.country.toLowerCase().includes(search.toLowerCase());
      const matchesRegion = regionFilter === "All" || etf.region === regionFilter;
      return matchesSearch && matchesRegion;
    });
  }, [data, search, regionFilter]);

  if (loading) return (
    <div className="flex flex-col h-[400px] items-center justify-center space-y-4">
      <Loader2 className="animate-spin text-emerald-500 h-8 w-8" />
      <p className="text-zinc-500 text-base">Quantifying global markets...</p>
    </div>
  );

  const cfg = ETF_SETS[currentSet];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Controls Bar */}
      <div className="flex flex-wrap items-center gap-4 bg-zinc-950/50 p-4 rounded-xl border border-zinc-800/50">
        <div className="flex bg-zinc-900 p-1 rounded-lg border border-zinc-800">
          <Button 
            variant={currentSet === "intl" ? "secondary" : "ghost"} 
            size="sm" 
            className="h-8 text-sm font-bold"
            onClick={() => setCurrentSet("intl")}
          >
            International
          </Button>
          <Button 
            variant={currentSet === "us" ? "secondary" : "ghost"} 
            size="sm" 
            className="h-8 text-sm font-bold"
            onClick={() => setCurrentSet("us")}
          >
            US Sectors
          </Button>
        </div>

        <div className="h-8 w-[1px] bg-zinc-800 mx-2 hidden sm:block" />

        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold text-zinc-500 uppercase tracking-widest">{cfg.regionLabel}</span>
          <select 
            className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm rounded-md px-2 py-1 outline-none focus:border-emerald-500 transition-colors"
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
          >
            <option value="All">All {cfg.label}</option>
            {cfg.groups.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-zinc-500" />
          <input
            type="text"
            placeholder="Search ticker or country..."
            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm rounded-md pl-9 pr-3 py-1.5 outline-none focus:border-emerald-500 transition-colors"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex bg-zinc-900 p-1 rounded-lg border border-zinc-800">
          <Button 
            variant={viewMode === "heatmap" ? "secondary" : "ghost"} 
            size="sm" 
            className="h-8 px-3"
            onClick={() => setViewMode("heatmap")}
            title="Heatmap View"
          >
            <BarChart2 className="h-4 w-4" />
          </Button>
          <Button 
            variant={viewMode === "table" ? "secondary" : "ghost"} 
            size="sm" 
            className="h-8 px-3"
            onClick={() => setViewMode("table")}
            title="Table View"
          >
            <TrendingUp className="h-4 w-4" />
          </Button>
          <Button 
            variant={viewMode === "both" ? "secondary" : "ghost"} 
            size="sm" 
            className="h-8 px-3 text-[12px] font-bold"
            onClick={() => setViewMode("both")}
          >
            BOTH
          </Button>
        </div>
      </div>

      {/* Heatmap Section */}
      {(viewMode === "heatmap" || viewMode === "both") && (
        <div className={viewMode === "both" ? "animate-in slide-in-from-left duration-500" : "animate-in zoom-in-95 duration-500"}>
          <Card className="bg-zinc-950 border-zinc-800 overflow-hidden shadow-2xl">
            <CardHeader className="py-3 px-4 border-b border-zinc-800 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                <BarChart2 className="h-4 w-4 text-emerald-500" />
                Performance Heatmap
              </CardTitle>
              <div className="flex items-center gap-1.5 bg-zinc-900 p-1 rounded-md border border-zinc-800">
                <span className="text-[11px] font-bold text-zinc-600 mr-1 ml-1 uppercase">Size By</span>
                {['day', 'week', 'month'].map(p => (
                  <Button 
                    key={p} 
                    variant={sizeBy === p ? "secondary" : "ghost"} 
                    size="sm" 
                    className="h-6 px-2 text-[11px] uppercase font-bold"
                    onClick={() => setSizeBy(p)}
                  >
                    {p}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent className="p-0">
               <ETFHeatmap 
                 data={filteredData} 
                 sizeBy={sizeBy} 
                 currentSet={currentSet} 
                 onTileClick={handleTileClick} 
               />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table Section */}
      {(viewMode === "table" || viewMode === "both") && (
        <div className={viewMode === "both" ? "animate-in slide-in-from-right duration-500" : "animate-in zoom-in-95 duration-500"}>
          <Card className="bg-zinc-950 border-zinc-800 shadow-xl overflow-hidden">
            <CardHeader className="py-3 px-4 border-b border-zinc-800">
              <CardTitle className="text-sm font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-500" />
                Leaderboard & Momentum
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ETFTable 
                data={filteredData} 
                currentSet={currentSet} 
                selectedTicker={selectedTicker} 
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Comparison Section (Only in Both or Table) */}
      {(viewMode === "both" || viewMode === "table") && (
        <div className="animate-in slide-in-from-bottom duration-500">
          <Card className="bg-zinc-950 border-zinc-800 shadow-xl overflow-hidden">
            <CardHeader className="py-3 px-4 border-b border-zinc-800">
              <CardTitle className="text-sm font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                 <BarChart2 className="h-4 w-4 text-emerald-500" />
                 Performance Comparison
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ETFComparisonChart data={data} spyYearly={spyYearly} />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ETFIntelligence;
